import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { PageError } from '@/components/Error';
import Loader from '@/components/Loader';
import useBodyHeightStyles from '@/hooks/useBodyHeightStyles';
import ErrorAccess from '@/modules/corporate/components/ErrorAccess';
import { useEmbeddedSurveyEditor } from '@/modules/corporate/modules/course/components/SurveyCardContainer/components/SurveyEditorModal/context';

import SurveyEditorContent from './components/SurveyEditorContent';
import SurveyEditorHeader from './components/SurveyEditorHeader';
import SurveyEditorSidebar from './components/SurveyEditorSidebar';
import { SurveyEditorProvider } from './context/SurveyEditorContext';
import useSurveyEditor from './hooks/useSurveyEditor';
import { SurveyEditorProps } from './types';

function SurveyEditor({ contentId }: SurveyEditorProps) {
  const { t } = useTranslation(['SurveyEditor', 'Database', 'Course', 'Tags']);
  const { bodyMaxHeightStyles } = useBodyHeightStyles();
  const embeddedEditor = useEmbeddedSurveyEditor();

  const {
    error,
    fields,
    fetching,
    isPublished,
    isSaving,
    isFieldPublished,
    survey,
    uiState,
    onAction,
    initialSurvey,
    formKey,
    onClosePublishChangesDialog,
    onPublishSubmit,
    onPublish,
    onRevertSuccess,
    getFieldAttributes,
    adaptedFields,
    allowMinorVersionPublish,
  } = useSurveyEditor({ contentId });

  if (fetching) {
    return <Loader />;
  }

  if (error) {
    return <PageError error={error} />;
  }

  if (!uiState.isOfSameWorkspace || !initialSurvey || initialSurvey.sharedByWorkspace) {
    return <ErrorAccess resource={t('Common:ResourceTypeSM.Survey')} />;
  }

  if (!survey) {
    return <Loader />;
  }

  return (
    <SurveyEditorProvider
      adaptedFields={adaptedFields}
      fields={fields}
      formKey={formKey}
      getFieldAttributes={getFieldAttributes}
      initialSurvey={initialSurvey}
      isFieldPublished={isFieldPublished}
      isPublished={isPublished}
      isSaving={Boolean(isSaving || uiState.isPublishing)}
      survey={survey}
      uiState={uiState}
      onAction={onAction}>
      <SurveyEditorHeader
        allowMinorVersionPublish={allowMinorVersionPublish}
        initialSurvey={initialSurvey}
        isSaving={isSaving}
        showPublishPopover={uiState.showPublishChangesDialog}
        survey={survey}
        onAction={onAction}
        onClose={onClosePublishChangesDialog}
        onPublish={onPublish}
        onPublishSubmit={onPublishSubmit}
        onRevertSuccess={onRevertSuccess}
      />
      <Stack
        className={cn('h-full overflow-hidden', embeddedEditor ? 'min-h-0' : bodyMaxHeightStyles)}>
        <SurveyEditorContent
          adaptedFields={adaptedFields}
          getFieldAttributes={getFieldAttributes}
          isFieldPublished={isFieldPublished}
          survey={survey}
          uiState={uiState}
          onAction={onAction}
        />
        <SurveyEditorSidebar survey={survey} />
      </Stack>
    </SurveyEditorProvider>
  );
}

export default SurveyEditor;
